import React from 'react';

export interface ScoreGaugeProps {
  score: number;
  maxScore?: number;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const getScoreColor = (score: number, maxScore: number): string => {
  const percentage = (score / maxScore) * 100;
  if (percentage >= 70) return '#10b981'; // green
  if (percentage >= 40) return '#f59e0b'; // yellow/amber
  return '#ef4444'; // red
};

const getScoreLabel = (score: number, maxScore: number): string => {
  const percentage = (score / maxScore) * 100;
  if (percentage >= 70) return 'Excellent';
  if (percentage >= 40) return 'Average';
  return 'Needs Improvement';
};

const sizeConfig = {
  sm: {
    size: 80,
    strokeWidth: 6,
    fontSize: 'text-xl',
    labelSize: 'text-xs',
  },
  md: {
    size: 120,
    strokeWidth: 8,
    fontSize: 'text-3xl',
    labelSize: 'text-sm',
  },
  lg: {
    size: 160,
    strokeWidth: 10,
    fontSize: 'text-4xl',
    labelSize: 'text-base',
  },
};

export const ScoreGauge: React.FC<ScoreGaugeProps> = ({
  score,
  maxScore = 5,
  size = 'md',
  className = '',
}) => {
  const config = sizeConfig[size];
  const normalizedScore = Math.min(Math.max(score, 0), maxScore);
  const percentage = (normalizedScore / maxScore) * 100;
  const color = getScoreColor(normalizedScore, maxScore);
  const label = getScoreLabel(normalizedScore, maxScore);

  // SVG circle calculations
  const center = config.size / 2;
  const radius = (config.size - config.strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div className={`inline-flex flex-col items-center ${className}`}>
      <svg
        width={config.size}
        height={config.size}
        className="transform -rotate-90"
      >
        {/* Background circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={config.strokeWidth}
        />
        {/* Progress circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={config.strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className="transition-all duration-500 ease-out"
        />
        {/* Score text (rotated back to normal) */}
        <text
          x={center}
          y={center}
          textAnchor="middle"
          dominantBaseline="central"
          className={`${config.fontSize} font-bold transform rotate-90`}
          fill={color}
          style={{ transform: 'rotate(90deg)', transformOrigin: 'center' }}
        >
          {normalizedScore}
        </text>
      </svg>
      <div className="mt-2 text-center">
        <div className={`${config.labelSize} font-medium text-gray-600`}>
          {label}
        </div>
        <div className={`${config.labelSize} text-gray-400`}>
          out of {maxScore}
        </div>
      </div>
    </div>
  );
};

ScoreGauge.displayName = 'ScoreGauge';

export default ScoreGauge;
